import { useLayoutEffect, type RefObject } from "react";

import { isRecoverableTimelineView } from "@/lib/timeline/view/TimelineViewRecovery/recovery";
import type { TimelineUiPreferences } from "@/lib/timeline/view/TimelineUiPreferences/prefs";
import type { TimelineViewTransform } from "@/lib/timeline/view/ViewTransform/transform";

interface StageApi {
  stageRef: RefObject<HTMLDivElement | null>;
  getViewTransform: () => TimelineViewTransform;
  resetToHome: () => void;
}

interface UseTimelineViewRecoveryOptions {
  ready: boolean;
  stage: StageApi;
  uiPrefs: TimelineUiPreferences;
  updateUiPrefs: (patch: Partial<TimelineUiPreferences>) => void;
}

/** Reset home + unlock when a prior session left the stage at min-zoom / NaN. */
export function useTimelineViewRecovery({
  ready,
  stage,
  uiPrefs,
  updateUiPrefs,
}: UseTimelineViewRecoveryOptions): void {
  const { stageRef, getViewTransform, resetToHome } = stage;
  useLayoutEffect(() => {
    if (!ready || stageRef.current === null) {
      return;
    }
    if (isRecoverableTimelineView(getViewTransform())) {
      return;
    }
    resetToHome();
    if (uiPrefs.cameraLockEnabled) {
      updateUiPrefs({ cameraLockEnabled: false });
    }
  }, [
    ready,
    getViewTransform,
    resetToHome,
    stageRef,
    uiPrefs.cameraLockEnabled,
    updateUiPrefs,
  ]);
}
